import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { PageHeader, StatusBadge, EmptyState } from "@/components/app/ui-bits";
import { Plus, Buildings, PencilSimple, Trash, Sparkle, CalendarBlank, Coins, ListChecks, CaretDown, CaretUp, MagnifyingGlass, Flag, MapTrifold } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth, hasMinRole } from "@/contexts/AuthContext";
import { can } from "@/lib/roles";
import AiExtractDialog from "@/components/app/AiExtractDialog";
import ProjectDeliverablesSheet from "@/components/app/ProjectDeliverablesSheet";
import { DesignInputsBadge } from "@/components/app/DesignInputsDrawer";
import ProjectFinanceChip from "@/components/app/ProjectFinanceChip";
import ProjectFinancialSnapshotPanel from "@/components/app/ProjectFinancialSnapshotPanel";
import ExportMenu from "@/components/app/ExportMenu";
import RecordInspector, { useRecordInspector } from "@/components/app/RecordInspector";
import StaffPicker from "@/components/app/StaffPicker";
import ProjectDeliverablesTree from "@/components/app/ProjectDeliverablesTree";
import ProjectTaskBreakdown from "@/components/app/ProjectTaskBreakdown";
import ProjectTabs from "@/components/app/ProjectTabs";
import ProjectLocationEditor, { LocationChip, locationLabel } from "@/components/app/ProjectLocationEditor";
import ProjectFinanceDashboard from "@/components/app/ProjectFinanceDashboard";
import PortfolioMap from "@/components/app/PortfolioMap";

const BLANK = {
  name: "", code: "", client: "", client_id: "", description: "", client_expectations: "",
  start_date: "", end_date: "", status: "active", budget: 0, deliverables: [], locations: [],
};

function DeliverableEditor({ deliverables, setDeliverables }) {
  const add = () => setDeliverables([...deliverables, { title: "", description: "", due_date: "", status: "pending", owner: "", reviewer: "", approver: "", contract_clause: "", milestone: false }]);
  const update = (i, k, v) => {
    const c = [...deliverables]; c[i] = { ...c[i], [k]: v }; setDeliverables(c);
  };
  const remove = (i) => setDeliverables(deliverables.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold text-slate-800">Deliverables</Label>
        <Button type="button" variant="outline" size="sm" onClick={add} data-testid="add-deliverable-button">
          <Plus size={12} className="mr-1" /> Add
        </Button>
      </div>
      {deliverables.length === 0 && (
        <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-center text-xs text-slate-500">
          No deliverables yet
        </div>
      )}
      {deliverables.map((d, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 rounded-md border border-slate-200 bg-slate-50/60 p-3">
          <Input className="col-span-12 md:col-span-5" placeholder="Title" value={d.title || ""} onChange={(e) => update(i, "title", e.target.value)} />
          <Input className="col-span-12 md:col-span-5" placeholder="Description" value={d.description || ""} onChange={(e) => update(i, "description", e.target.value)} />
          <Input className="col-span-6 md:col-span-2" type="date" value={d.due_date || ""} onChange={(e) => update(i, "due_date", e.target.value)} />
          <div className="col-span-6 md:col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Start date</Label>
            <Input type="date" value={d.start_date || ""} onChange={(e) => update(i, "start_date", e.target.value)} data-testid={`deliv-start-${i}`} />
          </div>
          <div className="col-span-6 md:col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Progress %</Label>
            <Input type="number" min={0} max={100} value={d.completion_pct ?? 0}
                   onChange={(e) => update(i, "completion_pct", Math.max(0, Math.min(100, parseInt(e.target.value || "0", 10))))}
                   data-testid={`deliv-progress-${i}`} />
          </div>
          <div className="col-span-12 md:col-span-4">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Sub-of (parent deliverable)</Label>
            <Select value={d.parent_deliverable_id || "__none__"} onValueChange={(v) => update(i, "parent_deliverable_id", v === "__none__" ? null : v)}>
              <SelectTrigger data-testid={`deliv-parent-${i}`}><SelectValue placeholder="None — top-level" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None — top-level deliverable</SelectItem>
                {deliverables.filter((x, j) => j !== i && x.id && x.title).map((x) => (
                  <SelectItem key={x.id} value={x.id}>↳ {x.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-12 md:col-span-4">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Owner (real HR staff) *</Label>
            <StaffPicker value={d.owner || ""} onChange={(id) => update(i, "owner", id)} testid={`deliv-owner-${i}`} placeholder="Search staff…" />
          </div>
          <div className="col-span-12 md:col-span-4">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Reviewer</Label>
            <StaffPicker value={d.reviewer || ""} onChange={(id) => update(i, "reviewer", id)} testid={`deliv-reviewer-${i}`} placeholder="Search reviewer…" />
          </div>
          <div className="col-span-12 md:col-span-4">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Approver</Label>
            <StaffPicker value={d.approver || ""} onChange={(id) => update(i, "approver", id)} testid={`deliv-approver-${i}`} placeholder="Search approver…" />
          </div>
          <div className="col-span-6 md:col-span-3">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Responsible party</Label>
            <Select value={d.responsible_type || "internal"} onValueChange={(v) => update(i, "responsible_type", v)}>
              <SelectTrigger data-testid={`deliv-resp-type-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="internal">Internal (staff)</SelectItem>
                <SelectItem value="external">External (subcontractor / vendor)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {d.responsible_type === "external" && (
            <div className="col-span-6 md:col-span-3">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">External party name</Label>
              <Input value={d.external_party || ""} onChange={(e) => update(i, "external_party", e.target.value)}
                     placeholder="Subcontractor / vendor" data-testid={`deliv-ext-party-${i}`} />
            </div>
          )}
          <div className={`col-span-12 ${d.responsible_type === "external" ? "md:col-span-6" : "md:col-span-9"}`}>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Depends on (must finish first)</Label>
            <Select value="__add__" onValueChange={(v) => {
              if (v !== "__add__" && !(d.depends_on || []).includes(v)) update(i, "depends_on", [...(d.depends_on || []), v]);
            }}>
              <SelectTrigger data-testid={`deliv-depends-${i}`}><SelectValue placeholder="Add a dependency…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__add__">Add a dependency…</SelectItem>
                {deliverables.filter((x, j) => j !== i && x.id && x.title && !(d.depends_on || []).includes(x.id)).map((x) => (
                  <SelectItem key={x.id} value={x.id}>{x.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(d.depends_on || []).length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {(d.depends_on || []).map((depId) => (
                  <span key={depId} className="inline-flex items-center gap-1 rounded border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-[10px] text-sky-700">
                    {deliverables.find((x) => x.id === depId)?.title || depId}
                    <button type="button" className="text-sky-500 hover:text-sky-800"
                            onClick={() => update(i, "depends_on", (d.depends_on || []).filter((z) => z !== depId))}
                            data-testid={`deliv-dep-remove-${i}-${depId}`}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <Input className="col-span-6 md:col-span-2" placeholder="Clause #" value={d.contract_clause || ""} onChange={(e) => update(i, "contract_clause", e.target.value)} />
          <Input className="col-span-6 md:col-span-2" type="date" placeholder="Client due" value={d.client_due_date || ""} onChange={(e) => update(i, "client_due_date", e.target.value)} />
          <Select value={d.priority || "medium"} onValueChange={(v) => update(i, "priority", v)}>
            <SelectTrigger className="col-span-6 md:col-span-2" data-testid={`deliv-priority-${i}`}><SelectValue placeholder="Priority" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
            </SelectContent>
          </Select>
          <Select value={d.status || "pending"} onValueChange={(v) => update(i, "status", v)}>
            <SelectTrigger className="col-span-6 md:col-span-2"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In progress</SelectItem>
              <SelectItem value="review">In review</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="blocked">Blocked</SelectItem>
            </SelectContent>
          </Select>
          <label className="col-span-5 md:col-span-3 flex items-center gap-2 text-xs text-slate-700">
            <input type="checkbox" checked={!!d.milestone} onChange={(e) => update(i, "milestone", e.target.checked)} />
            <span>Mark as milestone</span>
          </label>
          <Button type="button" variant="outline" className="col-span-1 h-9" onClick={() => remove(i)} aria-label="Remove">
            <Trash size={12} />
          </Button>
        </div>
      ))}
    </div>
  );
}

function ProjectForm({ initial, onSave, children }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial || BLANK);
  const [busy, setBusy] = useState(false);
  const [clients, setClients] = useState([]);
  useEffect(() => { if (open) setForm(initial || BLANK); }, [open, initial]);
  useEffect(() => {
    if (open) api.get("/clients").then((r) => setClients((r.data.items || []).filter((c) => !["rejected", "archived"].includes(c.status)))).catch(() => setClients([]));
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSave(form);
      setOpen(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="project-form-dialog">
        <DialogHeader><DialogTitle className="font-display">{initial?.id ? "Edit project" : "New project"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-6 gap-4">
          <div className="col-span-6 md:col-span-4">
            <Label>Project name *</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="project-name-input" />
          </div>
          <div className="col-span-6 md:col-span-2">
            <Label>Code</Label>
            <Input value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          </div>
          <div className="col-span-6 md:col-span-3">
            <Label>Client *</Label>
            {clients.length > 0 ? (
              <>
                <Select
                  value={form.client_id || "manual"}
                  onValueChange={(v) => {
                    if (v === "manual") { setForm({ ...form, client_id: "" }); return; }
                    const c = clients.find((x) => x.id === v);
                    setForm({ ...form, client_id: v, client: c ? c.name : form.client });
                  }}>
                  <SelectTrigger data-testid="project-client-select"><SelectValue placeholder="Pick from Clients register" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="manual">— Type manually (not in register) —</SelectItem>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.client_code ? ` · ${c.client_code}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
                {!form.client_id && (
                  <Input required className="mt-1" placeholder="Client name" value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} data-testid="project-client-input" />
                )}
              </>
            ) : (
              <Input required value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} data-testid="project-client-input" />
            )}
          </div>
          <div className="col-span-3 md:col-span-2">
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="on_hold">On hold</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-3 md:col-span-1">
            <Label>Budget (USD)</Label>
            <Input type="number" value={form.budget || 0} onChange={(e) => setForm({ ...form, budget: parseFloat(e.target.value || 0) })} />
          </div>
          <div className="col-span-3">
            <Label>Start date</Label>
            <Input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
          </div>
          <div className="col-span-3">
            <Label>End date</Label>
            <Input type="date" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
          </div>
          <div className="col-span-6">
            <ProjectLocationEditor locations={form.locations || []} setLocations={(v) => setForm({ ...form, locations: v })} />
          </div>
          <div className="col-span-6">
            <Label>Description</Label>
            <Textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="col-span-6">
            <Label>Client expectations</Label>
            <Textarea rows={3} value={form.client_expectations || ""} onChange={(e) => setForm({ ...form, client_expectations: e.target.value })} data-testid="project-expectations-input" />
          </div>
          <div className="col-span-6">
            <DeliverableEditor deliverables={form.deliverables || []} setDeliverables={(v) => setForm({ ...form, deliverables: v })} />
          </div>
          <DialogFooter className="col-span-6">
            <Button type="submit" disabled={busy} className="btn-primary-blue" data-testid="project-save-button">{busy ? "Saving…" : "Save project"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function progressPct(deliverables = []) {
  if (!deliverables.length) return 0;
  const done = deliverables.filter((d) => d.status === "completed").length;
  return Math.round((done / deliverables.length) * 100);
}

function daysBetween(start, end) {
  if (!start || !end) return null;
  const a = new Date(start).getTime();
  const b = new Date(end).getTime();
  if (isNaN(a) || isNaN(b)) return null;
  return Math.max(0, Math.round((b - a) / (1000 * 60 * 60 * 24)));
}

// (Inline Connections moved into ProjectTabs · `connections` tab)

export default function Projects() {
  const { user } = useAuth();
  const canEdit = can(user, "projects.edit") ?? hasMinRole(user, "manager");
  const canDelete = can(user, "projects.delete") ?? hasMinRole(user, "admin");
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const newId = searchParams.get("new");
  const highlightRef = useRef(null);
  const [tab, setTab] = useState("current"); // current | previous | all
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [showMap, setShowMap] = useState(false);
  const [search, setSearch] = useState("");
  const [locFilter, setLocFilter] = useState("all");
  const inspector = useRecordInspector();

  const isPrevious = (p) => {
    const st = (p.status || "").toLowerCase();
    if (["completed", "closed", "cancelled", "archived"].includes(st)) return true;
    // A project at P8 with all gates approved is effectively closed
    if (p.current_phase === "P8" && Array.isArray(p.phases) &&
        p.phases.every((ph) => ph.status === "approved")) return true;
    return false;
  };

  const filteredProjects = projects.filter((p) => {
    if (tab === "current" && isPrevious(p)) return false;
    if (tab === "previous" && !isPrevious(p)) return false;
    if (tab === "ai" && p.source !== "ai_extraction") return false;
    if (tab === "manual" && p.source === "ai_extraction") return false;
    if (locFilter !== "all") {
      const [kind, val] = locFilter.split("::");
      const locs = p.locations || [];
      if (kind === "country" && !locs.some((l) => (l.country || "") === val)) return false;
      if (kind === "state" && !locs.some((l) => (l.state || "") === val)) return false;
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      const locBlob = (p.locations || []).map(locationLabel).join(" ");
      const blob = `${p.name||""} ${p.code||""} ${p.client||""} ${p.client_name||""} ${locBlob}`.toLowerCase();
      if (!blob.includes(q)) return false;
    }
    return true;
  });

  // Location filter options built from live project data
  const locOptions = React.useMemo(() => {
    const countries = new Set(); const states = new Set();
    projects.forEach((p) => (p.locations || []).forEach((l) => {
      if (l.country) countries.add(l.country);
      if (l.country === "Somalia" && l.state) states.add(l.state);
    }));
    return { countries: [...countries].sort(), states: [...states].sort() };
  }, [projects]);

  const counts = {
    current:  projects.filter((p) => !isPrevious(p)).length,
    previous: projects.filter((p) => isPrevious(p)).length,
    all:      projects.length,
    ai:       projects.filter((p) => p.source === "ai_extraction").length,
    manual:   projects.filter((p) => p.source !== "ai_extraction").length,
  };

  const toggleExpanded = (id) => setExpandedIds((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const expandAll = () => setExpandedIds(new Set(filteredProjects.map((p) => p.id)));
  const collapseAll = () => setExpandedIds(new Set());

  const load = async () => {
    const { data } = await api.get("/projects");
    setProjects(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!newId || loading) return;
    // Auto-expand the newly created card
    setExpandedIds((s) => { const n = new Set(s); n.add(newId); return n; });
    // scroll after render
    const t = setTimeout(() => {
      highlightRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 300);
    // clear the query param after 6 s so reloads don't keep re-highlighting
    const clear = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }, 6000);
    return () => { clearTimeout(t); clearTimeout(clear); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newId, loading]);

  const save = async (form) => {
    if (form.id) {
      await api.patch(`/projects/${form.id}`, form);
      toast.success("Project updated");
    } else {
      await api.post("/projects", form);
      toast.success("Project created");
    }
    load();
  };

  const remove = async (id) => {
    try { await api.delete(`/projects/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  // Demo portfolio seeding has been moved entirely to Test Data Control —
  // see /admin/test-data. This keeps a single canonical seed/clear flow.

  return (
    <div data-testid="projects-page">
      <PageHeader
        eyebrow="Portfolio"
        title="Projects"
        description="All active projects with deliverables, client expectations, deadlines and timelines."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <ExportMenu module="projects" />
            {canEdit && (
              <>
                <AiExtractDialog
                  module="projects"
                  onSaved={load}
                  trigger={
                    <Button variant="outline" data-testid="projects-ai-extract-btn">
                      <Sparkle size={14} className="mr-2" weight="fill" /> AI Extract
                    </Button>
                  }
                />
              </>
            )}
            {canEdit && (
              <ProjectForm onSave={save}>
                <Button className="btn-primary-blue" data-testid="add-project-button">
                  <Plus size={14} className="mr-2" /> New project
                </Button>
              </ProjectForm>
            )}
          </div>
        }
      />
      <div className="mx-auto max-w-[1400px] p-4 md:p-8">
        {/* Tab + search + expand controls */}
        <div className="mb-5 flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5" data-testid="projects-tabs">
            {[
              { value: "current",  label: "Current",  count: counts.current },
              { value: "previous", label: "Previous", count: counts.previous },
              { value: "all",      label: "All",      count: counts.all },
              { value: "ai",       label: "AI Extracted", count: counts.ai },
              { value: "manual",   label: "Manual",   count: counts.manual },
            ].map((t) => (
              <button key={t.value} type="button" onClick={() => setTab(t.value)}
                data-testid={`projects-tab-${t.value}`}
                className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition rounded-sm ${
                  tab === t.value
                    ? (t.value === "ai" ? "bg-violet-600 text-white" : "bg-slate-900 text-white")
                    : (t.value === "ai" ? "text-violet-700 hover:bg-violet-50" : "text-slate-600 hover:bg-slate-50")
                }`}>
                {t.label}
                <span className={`ml-1.5 inline-flex min-w-[1.5rem] justify-center rounded-full px-1.5 text-[10px] ${
                  tab === t.value ? "bg-white/20" : "bg-slate-100 text-slate-600"
                }`}>{t.count}</span>
              </button>
            ))}
          </div>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, code, client, location…"
              className="h-9 w-72 pl-8 text-sm"
              data-testid="projects-search" />
          </div>
          {(locOptions.countries.length > 0 || locOptions.states.length > 0) && (
            <Select value={locFilter} onValueChange={setLocFilter}>
              <SelectTrigger className="h-9 w-56 text-sm" data-testid="projects-location-filter">
                <SelectValue placeholder="All locations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All locations</SelectItem>
                {locOptions.countries.map((c) => (
                  <SelectItem key={`country::${c}`} value={`country::${c}`}>🌍 {c}</SelectItem>
                ))}
                {locOptions.states.map((s) => (
                  <SelectItem key={`state::${s}`} value={`state::${s}`}>· {s} (Somalia)</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <div className="ml-auto flex items-center gap-1">
            <Button size="sm" variant={showMap ? "default" : "outline"} onClick={() => setShowMap((v) => !v)} data-testid="projects-map-toggle">
              <MapTrifold size={13} className="mr-1" /> Map
            </Button>
            <Button size="sm" variant="outline" onClick={expandAll} data-testid="projects-expand-all">Expand all</Button>
            <Button size="sm" variant="outline" onClick={collapseAll} data-testid="projects-collapse-all">Collapse all</Button>
          </div>
        </div>

        {showMap && <div className="mb-6"><PortfolioMap /></div>}

        {loading ? (
          <div className="font-mono-alt text-xs uppercase tracking-[0.25em] text-slate-500">Loading…</div>
        ) : filteredProjects.length === 0 ? (
          <EmptyState icon={Buildings} title={tab === "previous" ? "No previous projects" : tab === "current" ? "No current projects" : "No projects yet"}
            description={tab === "previous"
              ? "Completed, closed and cancelled projects will appear here."
              : tab === "current"
              ? "All your projects are active here. Switch to Previous to see archived ones."
              : "Create your first project to track deliverables and timelines — or use the AI Extractor to draft one from a contract."} />
        ) : (
          <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
            {filteredProjects.map((p) => {
              const pct = progressPct(p.deliverables);
              const totalDays = daysBetween(p.start_date, p.end_date);
              const isNew = p.id === newId;
              const isAi = p.source === "ai_extraction";
              const expanded = expandedIds.has(p.id);
              return (
                <article
                  key={p.id}
                  ref={isNew ? highlightRef : null}
                  className={`dc-card card-hover ${isNew ? "dc-highlight-new" : ""} ${isAi ? "dc-ai-project" : ""}`}
                  data-testid={`project-card-${p.id}`}
                  data-source={isAi ? "ai_extraction" : "manual"}
                >
                  {/* Head */}
                  <header
                    className="flex items-start gap-4 border-b border-slate-100 p-5 cursor-pointer hover:bg-slate-50/50 transition"
                    onClick={() => toggleExpanded(p.id)}
                    role="button"
                    aria-expanded={expanded}
                    data-testid={`project-toggle-${p.id}`}
                  >
                    <div
                      className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg font-mono-alt text-xs font-bold text-white"
                      style={{ background: isAi ? "linear-gradient(135deg,#7C3AED,#4F46E5)" : "var(--grad-blue)" }}
                      title={p.name}
                    >
                      {(p.code || p.name || "P").slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">
                          {p.code || "—"}
                        </span>
                        <StatusBadge status={p.status} />
                        {p.design_path && p.design_path !== "not_decided" && (
                          <DesignInputsBadge designPath={p.design_path} />
                        )}
                        {isAi && (
                          <span
                            className="dc-ai-badge"
                            title="This project was created via the AI Document Extractor. Review the fields before treating it as source of truth."
                            data-testid={`project-ai-badge-${p.id}`}
                          >
                            <Sparkle size={11} weight="fill" /> AI Extraction Project
                          </span>
                        )}
                      </div>
                      <h3 className="mt-1 font-display text-lg font-semibold leading-tight text-slate-900 md:text-xl">
                        {p.name || <span className="text-slate-400">Untitled project</span>}
                      </h3>
                      <div className="mt-1 text-sm text-slate-600">
                        Client <span className="font-medium text-slate-900">{p.client || "—"}</span>
                      </div>
                      {(p.locations || []).length > 0 && (
                        <div className="mt-1.5"><LocationChip project={p} /></div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); toggleExpanded(p.id); }}
                      className="flex h-8 w-8 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition"
                      data-testid={`project-expand-${p.id}`}
                      title={expanded ? "Collapse details" : "Expand to see deliverables, lifecycle and connections"}
                      aria-label={expanded ? "Collapse" : "Expand"}
                    >
                      {expanded ? <CaretUp size={16} weight="bold" /> : <CaretDown size={16} weight="bold" />}
                    </button>
                  </header>

                  {/* Stats strip */}
                  <dl className="grid grid-cols-3 divide-x divide-slate-100 bg-slate-50/60">
                    <div className="px-4 py-3">
                      <dt className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                        <CalendarBlank size={11} /> Timeline
                      </dt>
                      <dd className="mt-1 text-sm font-medium tabular-nums text-slate-800">
                        {formatDate(p.start_date)}
                      </dd>
                      <dd className="text-xs text-slate-500">
                        → {formatDate(p.end_date)} {totalDays != null && <span className="text-slate-400">· {totalDays}d</span>}
                      </dd>
                    </div>
                    <div className="px-4 py-3">
                      <dt className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                        <Coins size={11} /> Budget
                      </dt>
                      <dd className="mt-1 font-display text-lg font-bold tabular-nums text-slate-900">
                        {formatCurrency(p.budget)}
                      </dd>
                    </div>
                    <div className="px-4 py-3">
                      <dt className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                        <ListChecks size={11} /> Deliverables
                      </dt>
                      <dd className="mt-1 text-sm font-medium text-slate-800">
                        {(p.deliverables || []).length} total
                      </dd>
                      <dd className="text-xs text-slate-500">{pct}% complete</dd>
                    </div>
                  </dl>

                  {/* Progress bar */}
                  <div className="px-5 pt-4">
                    <div className="mb-1.5 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      <span>Progress</span>
                      <span className="tabular-nums">{pct}%</span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${pct}%`,
                          background: "linear-gradient(90deg, #1E5CBA, #4F8DEB)",
                        }}
                      />
                    </div>
                  </div>

                  {/* SOP P1-P8 phase strip */}
                  {Array.isArray(p.phases) && p.phases.length > 0 && (
                    <div className="px-5 pt-3">
                      <div className="mb-1.5 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                        <span>Lifecycle · current {p.current_phase || "P1"}</span>
                        <span className="tabular-nums">
                          {p.phases.filter((ph) => ph.status === "approved").length}/{p.phases.length} gates
                        </span>
                      </div>
                      <div className="flex gap-1">
                        {p.phases.map((ph) => (
                          <div
                            key={ph.phase_id}
                            title={`${ph.phase_id} · ${ph.name} · ${ph.status.replace(/_/g, " ")}`}
                            className={`h-2 flex-1 rounded ${ph.status === "approved" ? "bg-emerald-500" : ph.status === "in_progress" ? "bg-blue-500 ring-1 ring-blue-300" : ph.status === "gate_rejected" ? "bg-red-500" : "bg-slate-200"}`}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Financial snapshot — live-linked from Finance module */}
                  <div className="px-5 pt-4">
                    <ProjectFinanceChip projectId={p.id} />
                    <div className="mt-2">
                      <ProjectFinanceDashboard projectId={p.id} projectName={p.name} />
                    </div>
                  </div>

                  {expanded && (
                  <>
                  {/* Detailed Project Financial Snapshot — cross-module pull */}
                  <ProjectFinancialSnapshotPanel projectId={p.id} />

                  {/* Description */}
                  {p.description && (
                    <section className="px-5 py-4">
                      <div className="overline mb-1.5 text-slate-500">About</div>
                      <p className="prose-card whitespace-pre-line">{p.description}</p>
                    </section>
                  )}

                  {/* Client expectations */}
                  {p.client_expectations && (
                    <section className="mx-5 mb-4 rounded-lg border border-amber-200 bg-amber-50/70 p-3">
                      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-amber-800">
                        <Flag size={11} weight="fill" /> Client expects
                      </div>
                      <p className="mt-1 text-sm leading-relaxed text-amber-900">{p.client_expectations}</p>
                    </section>
                  )}

                  {/* Reporting + obligations — side by side if both present */}
                  {(p.reporting_requirements || p.major_obligations) && (
                    <section className="mx-5 mb-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                      {p.reporting_requirements && (
                        <div className="rounded-lg border border-slate-200 bg-white p-3">
                          <div className="overline text-slate-500">Reporting</div>
                          <p className="mt-1 text-sm leading-relaxed text-slate-700">{p.reporting_requirements}</p>
                        </div>
                      )}
                      {p.major_obligations && (
                        <div className="rounded-lg border border-slate-200 bg-white p-3">
                          <div className="overline text-slate-500">Major obligations</div>
                          <p className="mt-1 text-sm leading-relaxed text-slate-700">{p.major_obligations}</p>
                        </div>
                      )}
                    </section>
                  )}

                  {/* Deliverables sub-breakdown tree with drag-reorder + audit timeline */}
                  <ProjectDeliverablesTree
                    project={p}
                    canEdit={canEdit}
                    onRowClick={(d) => inspector.open(d, {
                      title: d.title || "Deliverable",
                      subtitle: `${p.name} · ${formatDate(d.due_date) || "no due date"}`,
                    })}
                    onReload={load}
                  />

                  {/* Task Breakdown — grouped tasks auto-synced from deliverables */}
                  <ProjectTaskBreakdown projectId={p.id} />

                  {/* 13 connected project tabs — Risk, Issues, CRQA, NCR, Lessons,
                      KPI, MPR, Time, Close-out, Docs, Connections, Design Inputs,
                      Internal Audit — all driven by real backend data. */}
                  <ProjectTabs project={p} onReload={load} />

                  {/* (Connections panel is now inside ProjectTabs · `connections` tab) */}
                  </>
                  )}

                  {/* Footer actions — Deliverables (primary) + Edit/Delete */}
                  {canEdit && (
                    <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 bg-slate-50/50 px-5 py-3">
                      <ProjectDeliverablesSheet
                        project={p}
                        canEdit={canEdit}
                        onReload={load}
                        onOpenEdit={() => {
                          // Open the full project Edit dialog by clicking the
                          // existing Edit trigger. We do this by toggling a
                          // synthetic state via the existing ProjectForm modal.
                          const edit = document.querySelector(`[data-testid="edit-project-${p.id}"]`);
                          if (edit) edit.click();
                        }}
                      />
                      <ProjectForm initial={p} onSave={(f) => save({ ...f, id: p.id })}>
                        <Button variant="outline" size="sm" data-testid={`edit-project-${p.id}`}>
                          <PencilSimple size={12} className="mr-1" /> Edit
                        </Button>
                      </ProjectForm>
                      {canDelete && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" data-testid={`delete-project-${p.id}`}>
                              <Trash size={12} />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete project?</AlertDialogTitle>
                              <AlertDialogDescription>
                                <strong>{p.name}</strong> will be permanently removed along with all its deliverables.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(p.id)}>Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </footer>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>
      <RecordInspector {...inspector.props} />
    </div>
  );
}
